<template>
  <div class="form-group">
    <label :for="name">
      {{ props.label ? t(`labels.${props.label}`) : t(`labels.${props.name}`) }}
    </label>

    <Select
      :id="name"
      :key="locale"
      v-model="internalValue"
      :options="items"
      :filter="props.filter"
      :showClear="props.showClear ?? false"
      optionLabel="name"
      optionValue="id"
      :placeholder="
        props.placeholder ? t(`placeholder.${props.placeholder}`, props.placeholder) : '&nbsp'
      "
      :class="[
        'w-full',
        {
          'error-state': showError,
        },
      ]"
      :disabled="disabled"
      :virtualScrollerOptions="{
        lazy: true,
        onLazyLoad: onLazyLoad,
        itemSize: 38,
        showLoader: false,
        delay: 0,
      }"
      @change="handleChange"
      @filter="handleFilter"
    >
      <template #value="slotProps">
        <!-- Shows selected value -->
        <div v-if="slotProps.value && selectedItem" class="flex align-items-center">
          {{ selectedItem.name }}
        </div>
      </template>
      <template #option="slotProps">
        {{ slotProps.option.name }}
      </template>
    </Select>
    <FormError v-if="showError" :message="errorMessage" />
  </div>
</template>

<script lang="ts" setup generic="T extends { id: string | number; name: string }">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import type { SelectChangeEvent } from 'primevue/select';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import {
  type LazySelectFetchOptions,
  type PaginatedResponse,
  useLazySelect,
} from '@/components/forms/hooks/useLazySelect.ts';

import FormError from './errors/FormError.vue';
import type { SelectModel } from './FormSelect.vue';

const props = defineProps<{
  name: string;
  placeholder?: string;
  modelValue: string | undefined;
  initialValue: string | undefined;
  isDirty: (dirty: boolean) => void;
  label?: string;
  disabled?: boolean;
  filter?: boolean;
  showClear?: boolean;
  // Function to fetch paginated data
  fetchPagedData: (
    options: LazySelectFetchOptions,
  ) => Promise<PaginatedResponse<T> | undefined> | undefined;
  // Function to fetch single item by ID (for initial value)
  fetchSingleItem?: (id: string) => Promise<T | null | undefined>;
  // Items per page (default: 50)
  itemsPerPage?: number;
}>();

const { t, locale } = useI18n();
const { errorMessage, meta, validate, setTouched } = useField<string>(props.name, undefined, {
  // do not validate on mount
  validateOnMount: false,
  // do not auto-validate on every model change (we control it)
  validateOnValueUpdate: false,
});

const { submitCount } = useForm();
const showError = computed(() => {
  if (props.disabled) return false;
  if (!errorMessage.value) return false;

  // Show error only:
  // - after user touched the field, or
  // - after the form has been submitted at least once
  return meta.touched || submitCount.value > 0;
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | undefined): void;
  (e: 'change', payload: SelectChangeEvent | string | undefined): void;
  (e: 'userChange', payload: SelectChangeEvent): void;
}>();

// Use the composable for lazy loading logic
const { items, onLazyLoad, loadInitialItems, loadInitialValue, handleSearch, reset } =
  useLazySelect<T>({
    fetchPagedData: () => props.fetchPagedData,
    fetchSingleItem: props.fetchSingleItem,
    itemsPerPage: props.itemsPerPage ?? 50,
  });

const internalValue = ref<string | undefined>(props.modelValue);
const selectedItem = ref<SelectModel | null>(null);
const isFieldDirty = ref(false); // Track dirty state
const isUpdatingFromFilter = ref(false); // Prevent infinite loops
const isProgrammaticUpdate = ref(false); // Track programmatic updates
const filterTimeoutId = ref<number | null>(null);

const handleFilter = (event: { value: string }) => {
  // Clear existing timeout
  if (filterTimeoutId.value !== null) {
    clearTimeout(filterTimeoutId.value);
  }

  // Set new timeout to wait for user to finish typing
  filterTimeoutId.value = window.setTimeout(() => {
    void handleSearch(event.value || null);
    filterTimeoutId.value = null;
  }, 500); // Wait 500ms after user stops typing
};

// Watch for changes in modelValue and update internal state
watch(
  () => props.modelValue,
  async (newValue) => {
    // Don't interfere while updating from filter change
    if (!isUpdatingFromFilter.value && !isProgrammaticUpdate.value) {
      internalValue.value = newValue;
      if (newValue) {
        const item = items.value.find((i) => String(i.id) === newValue);
        if (item) {
          selectedItem.value = item;
        } else {
          // Item not in list, try to fetch it
          const fetchedItem = await loadInitialValue(newValue);
          selectedItem.value = fetchedItem || null;
        }
      } else {
        selectedItem.value = null;
      }
    }
  },
);

// Watch internalValue changes and emit
watch(internalValue, async (newValue) => {
  if (!isProgrammaticUpdate.value) {
    emit('update:modelValue', newValue);
    emit('change', newValue);

    if (newValue) {
      const item = items.value.find((i) => String(i.id) === newValue);
      if (item) {
        selectedItem.value = item;
      } else {
        // Item not in list, try to fetch it
        const fetchedItem = await loadInitialValue(newValue);
        selectedItem.value = fetchedItem || null;
      }
    } else {
      selectedItem.value = null;
    }
  }
});

// Compare the current value with initial value for dirty tracking
watch([internalValue, () => props.initialValue], () => {
  const isDirty = internalValue.value !== props.initialValue;
  isFieldDirty.value = isDirty;
  props.isDirty(isDirty);
});

// Watch for changes in fetchPagedData function (e.g., when filter changes)
// This enables reactive filtering when the parent component changes the fetch function
watch(
  () => props.fetchPagedData,
  async (newFetch, oldFetch) => {
    // Only reload if function actually changed (not on initial mount)
    if (newFetch !== oldFetch && oldFetch !== undefined && !isUpdatingFromFilter.value) {
      isUpdatingFromFilter.value = true;

      reset();
      await loadInitialItems();

      // Use nextTick to ensure items are fully loaded
      await nextTick();

      // Use programmatic update flag to prevent watchers from interfering
      isProgrammaticUpdate.value = true;

      // Clear selection if current value is not in the new items list
      if (internalValue.value) {
        const itemExists = items.value.some((i) => String(i.id) === internalValue.value);
        if (!itemExists) {
          internalValue.value = undefined;
          selectedItem.value = null;
        }
      }

      // Auto-select if there's exactly one item
      if (items.value.length === 1 && !internalValue.value) {
        const item = items.value[0];
        internalValue.value = String(item.id);
        selectedItem.value = item;
      }

      // Reset flags
      isProgrammaticUpdate.value = false;
      isUpdatingFromFilter.value = false;

      // Manually emit after flags are reset
      if (internalValue.value) {
        emit('update:modelValue', internalValue.value);
        emit('change', internalValue.value);
      }
    }
  },
);

// Auto-select the only item when there's exactly one option (only on initial load)
watch(
  () => items.value,
  (newItems) => {
    if (!isUpdatingFromFilter.value && newItems.length === 1 && !internalValue.value) {
      internalValue.value = String(newItems[0].id);
      selectedItem.value = newItems[0];
    }
  },
);

onMounted(async () => {
  await loadInitialItems();
  const initialItem = await loadInitialValue(props.initialValue);
  if (initialItem) {
    selectedItem.value = initialItem;
    internalValue.value = String(initialItem.id);
  }
});

onUnmounted(() => {
  // Clean up any pending filter timeout
  if (filterTimeoutId.value !== null) {
    clearTimeout(filterTimeoutId.value);
  }
});

function handleChange(event: SelectChangeEvent) {
  // mark as touched & validate only on real user interaction
  setTouched(true);
  void validate();

  emit('change', event);
  emit('userChange', event); // Use this in case you only want to subscribe for user changes
}
</script>
